<template>
  <div class="detail-container py-5">
    <!-- ローディング画面 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p class="mt-2">PDF出力中...</p>
    </div>

    <div class="card mx-auto shadow-lg">
      <div class="card-body">
        <div ref="pdfContent">
          <div v-if="measurement_report">
            <div v-if="measurement_report.m_customer">
              <div v-if="measurement_report.m_building">
                <!-- 表紙 -->
                <div class="main-cover page-break">
                  <div class="thank-you d-flex align-items-center">
                    <p v-if="measurement_report.dear_nm" v-bind:style="{ 'white-space': 'pre-line' }">{{ measurement_report.dear_nm }}&nbsp;御中</p>
                  </div>
                  <h1 class="text-center"><div class="report-title">空気環境測定結果報告書</div></h1>
                  <div class="contents">
                    <h4 class="d-flex align-items-top"><span class="subtitle">物件名称</span><span class="information">{{ measurement_report.m_building.building_nm }}</span></h4>
                    <h4 class="d-flex align-items-top"><span class="subtitle">住所</span><span class="information">{{ measurement_report.m_building.prefecture }}{{ measurement_report.m_building.city }}{{ measurement_report.m_building.other_address }}</span></h4>
                    <h4 class="d-flex align-items-top"><span class="subtitle">測定日</span><span class="information">{{ formatDatePattern1(measurement_report.measure_dt) }}</span></h4>
                    <h4 v-if="measurement_report.rpt_company_nm" class="d-flex align-items-top"><span class="subtitle">報告社名</span><span class="information">{{ measurement_report.rpt_company_nm }}</span></h4>
                    <h4 v-if="measurement_report.rpt_company_address" class="d-flex align-items-top"><span class="subtitle">報告社住所</span>
                      <span class="information">{{ measurement_report.rpt_company_address }}
                      <br v-if="measurement_report.rpt_company_address02">&nbsp;&nbsp;&nbsp;{{ measurement_report.rpt_company_address02 }}</span>
                    </h4>
                    <h4 v-if="measurement_report.regist_no" class="d-flex align-items-top"><span class="subtitle">登録番号</span><span class="information">{{ measurement_report.regist_no }}</span></h4>
                  </div>
                </div>
                <!-- 表紙 -->

                <!-- 空気環境調査概要 -->
                <div class="overview page-break">
                  <div class="title-contents d-flex align-items-center justify-content-between">
                    <h2 class="text-center">空気環境調査概要</h2>
                    <div>
                      <table class="table table-bordered">
                        <tr>
                          <th>ビル名</th>
                          <td>{{ measurement_report.m_building.building_nm }}</td>
                        </tr>
                        <tr>
                          <th>測定年月日</th>
                          <td>{{ formatDatePattern1(measurement_report.measure_dt) }}</td>
                        </tr>
                        <tr>
                          <th>実施者</th>
                          <td>{{ measurement_report.measure_person }}</td>
                        </tr>
                        <tr>
                          <th>補助者</th>
                          <td>{{ measurement_report.assistant }}</td>
                        </tr>
                      </table>
                    </div>
                  </div>

                  <!-- 概要 -->
                  <div class="main-contents page-break">
                    <table class="table table-bordered">
                      <thead>
                        <tr>
                          <th class="major-head" colspan="4">建築物環境衛生管理基準</th>
                          <th class="major-head" colspan="2">測定値</th>
                          <th rowspan="2">不適箇所数<br>／<br>測定箇所数</th>
                          <th class="major-head comment-title" rowspan="2" colspan="5">概評</th>
                        </tr>
                        <tr>
                          <th colspan="2">測定項目</th>
                          <th colspan="2">基準値</th>
                          <th>最高値</th>
                          <th>最低値</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="major-body" rowspan="3">温熱条件</td>
                          <td>温度</td>
                          <td class="major-body" rowspan="3">瞬間値</td>
                          <td>{{ measurement_report.t_measure_result.temperture_min_std }} 〜 {{ measurement_report.t_measure_result.temperture_max_std }} ℃</td>
                          <td>{{ measurement_report.t_measure_result.temperture_max_measure }} ℃</td>
                          <td>{{ measurement_report.t_measure_result.temperture_min_measure }} ℃</td>
                          <td>{{ measurement_report.t_measure_result.temperture_inappropriate_points }} ／ {{ measurement_report.t_measure_result.temperture_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.temperture_comment }}</td>
                        </tr>
                        <tr>
                          <td>相対湿度</td>
                          <td>{{ measurement_report.t_measure_result.relative_humidity_min_std }} 〜 {{ measurement_report.t_measure_result.relative_humidity_max_std }} %</td>
                          <td>{{ measurement_report.t_measure_result.relative_humidity_max_measure }} %</td>
                          <td>{{ measurement_report.t_measure_result.relative_humidity_min_measure }} %</td>
                          <td>{{ measurement_report.t_measure_result.relative_humidity_inappropriate_points }} ／ {{ measurement_report.t_measure_result.relative_humidity_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.relative_humidity_comment }}</td>
                        </tr>
                        <tr>
                          <td>気流</td>
                          <td>{{ measurement_report.t_measure_result.air_current_max_std }} m/sec 以下</td>
                          <td>{{ measurement_report.t_measure_result.air_current_max_measure }} m/sec</td>
                          <td>{{ measurement_report.t_measure_result.air_current_min_measure }} m/sec</td>
                          <td>{{ measurement_report.t_measure_result.air_current_inappropriate_points }} ／ {{ measurement_report.t_measure_result.air_current_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.air_current_comment }}</td>
                        </tr>
                        <tr>
                          <td class="major-body" rowspan="3">空気清浄度</td>
                          <td>二酸化炭素(CO<span>2</span>)</td>
                          <td class="major-body" rowspan="3">平均値</td>
                          <td>{{ measurement_report.t_measure_result.co2_max_std }} ppm 以下</td>
                          <td>{{ measurement_report.t_measure_result.co2_max_measure }} ppm</td>
                          <td>{{ measurement_report.t_measure_result.co2_min_measure }} ppm</td>
                          <td>{{ measurement_report.t_measure_result.co2_inappropriate_points }} ／ {{ measurement_report.t_measure_result.co2_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.co2_comment }}</td>
                        </tr>
                        <tr>
                          <td>一酸化炭素(CO)</td>
                          <td>{{ measurement_report.t_measure_result.co_max_std }} ppm 以下</td>
                          <td>{{ measurement_report.t_measure_result.co_max_measure }} ppm</td>
                          <td>{{ measurement_report.t_measure_result.co_min_measure }} ppm</td>
                          <td>{{ measurement_report.t_measure_result.co_inappropriate_points }} ／ {{ measurement_report.t_measure_result.co_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.co_comment }}</td>
                        </tr>
                        <tr>
                          <td>浮遊粉塵量</td>
                          <td>{{ measurement_report.t_measure_result.float_powder_dust_max_std }} m/㎥ 以下</td>
                          <td>{{ measurement_report.t_measure_result.float_powder_dust_max_measure }} m/㎥</td>
                          <td>{{ measurement_report.t_measure_result.float_powder_dust_min_measure }} m/㎥</td>
                          <td>{{ measurement_report.t_measure_result.float_powder_dust_inappropriate_points }} ／ {{ measurement_report.t_measure_result.float_powder_dust_all_points }}</td>
                          <td class="comment" colspan="5">{{ measurement_report.t_measure_result.float_powder_dust_comment }}</td>
                        </tr>
                        <tr class="special-comment-area">
                          <td class="special-comment" colspan="12">
                            特記事項<br>
                            <div v-bind:style="{ 'white-space': 'pre-line' }">
                              {{measurement_report.special_comment}}
                            </div>
                            <div v-if="measurement_report.sign_flg == 1" class="sign-box01"></div>
                            <div v-if="measurement_report.sign_flg == 1" class="sign-box02"></div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <!-- 空気環境調査概要 -->

                <!-- 作業場所と測定機器 -->
                <div class="place-and-device d-flex justify-content-center page-break">
                  <!-- 測定場所 -->
                  <div class="measure-place mx-3">
                    <table class="table table-bordered">
                      <thead>
                        <tr>
                          <th class="place-title" colspan="3">測定場所</th>
                        </tr>
                        <tr>
                          <th>No.</th>
                          <th>階</th>
                          <th class="place-name">室名</th>
                        </tr>
                      </thead>
                      <template v-if="insideAirWorkPlaces">
                        <tbody>
                          <tr v-for="(t_measurement_work_place, index) in workPlaceMinimumRows" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>{{ t_measurement_work_place?.floor_no || '' }}</td>
                            <td class="place-name">{{ t_measurement_work_place?.work_place_nm || '' }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </table>

                    <table class="table table-bordered my-2">
                      <tr>
                        <th>{{ measurement_report.m_building.building_nm }}</th>
                        <td>計</td>
                        <td>{{ measurement_report.area_point }}ポイント</td>
                      </tr>
                    </table>
                  </div>

                  <!-- 測定機器 -->
                  <div class="measure-device mx-3">
                    <table class="table table-bordered">
                      <thead>
                        <tr>
                          <th class="device-title" colspan="3">測定機器</th>
                        </tr>
                        <tr>
                          <th>測定項目</th>
                          <th>測定機器</th>
                          <th class="device-contents">製造者・型式・製造番号・仕様</th>
                        </tr>
                      </thead>
                      <template v-if="measurement_report.t_measurement_used_devices">
                        <tbody>
                          <tr v-for="(t_measurement_used_device, index) in deviceMinimumRows" :key="index">
                            <td>{{ t_measurement_used_device?.m_measure_type?.measure_type_nm || '' }}</td>
                            <td>{{ t_measurement_used_device?.m_device?.device_nm || '' }}</td>
                            <td class="device-contents">
                              {{ t_measurement_used_device?.m_device?.manufacuturer || '' }}<br>
                              {{ t_measurement_used_device?.m_device?.model_nm || '' }}
                              <span v-if="t_measurement_used_device?.m_measure_type?.id == 6" class="ms-3">
                                {{ t_measurement_used_device?.m_device?.serial_no || '' }}
                              </span>
                              <br>
                              {{ t_measurement_used_device?.m_device?.specification || '' }}
                            </td>
                          </tr>

                        </tbody>
                      </template>
                    </table>
                  </div>
                </div>
                <!-- 作業場所と測定機器 -->

                <!-- 環境測定結果 -->
                <div class="measure-result">
                  <div class="title-area d-flex aling-items-center justify-content-between">
                    <h2 class="text-center">環境測定結果</h2>
                    <table class="table">
                      <tr>
                        <th>建築物名</th>
                        <td colspan="3">{{ measurement_report.m_building.building_nm }}</td>
                      </tr>
                      <tr>
                        <th>測定年月日</th>
                        <td>{{ formatDatePattern1(measurement_report.measure_dt) }}</td>
                        <th class="weather">天候</th>
                        <td>{{ measurement_report.weather }}</td>
                      </tr>
                    </table>
                  </div>

                  <div v-for="(viewSeparateWorkPlace, idx) in viewSeparateWorkPlaces" :key="idx">
                    <div class="measure-contents mb-3" :class="{ 'pt-80': idx !== 0 }">
                      <table class="table contents-table">
                        <thead class="text-center">
                          <tr>
                            <th rowspan="4" class="text-nowrap work-place">測定場所</th>
                            <th colspan="3" class="text-nowrap">測定状況</th>
                            <th colspan="2" class="text-nowrap">温度</th>
                            <th class="text-nowrap relative-humidity">相対湿度</th>
                            <th class="text-nowrap air-current">気流</th>
                            <th colspan="2" class="text-nowrap">二酸化炭素</th>
                            <th colspan="2" class="text-nowrap">一酸化炭素</th>
                            <th colspan="2" class="text-nowrap">浮遊粉塵量</th>
                            <th colspan="3" class="text-nowrap">その他の事項</th>
                            <th rowspan="4" class="text-nowrap remarks">備考</th>
                          </tr>
                          <tr>
                            <th rowspan="2" class="text-nowrap time">時間</th>
                            <th rowspan="2" class="text-nowrap person-count">在室<br>人数</th>
                            <th rowspan="2" class="text-nowrap person-count">喫煙<br>者数</th>
                            <th colspan="2" class="text-nowrap">{{ measurement_report.t_measure_result.temperture_min_std }}〜{{ measurement_report.t_measure_result.temperture_max_std }} ℃</th>
                            <th class="text-nowrap relative_humidity">{{ measurement_report.t_measure_result.relative_humidity_min_std }}〜{{ measurement_report.t_measure_result.relative_humidity_max_std }} %</th>
                            <th rowspan="2" class="text-nowrap air-current">{{ measurement_report.t_measure_result.air_current_max_std }}m/sec<br>以下</th>
                            <th colspan="2" class="text-nowrap">{{ measurement_report.t_measure_result.co2_max_std }}ppm以下</th>
                            <th colspan="2" class="text-nowrap">{{ measurement_report.t_measure_result.co_max_std }}ppm以下</th>
                            <th colspan="2" class="text-nowrap">{{ measurement_report.t_measure_result.float_powder_dust_max_std }}mg/㎡以下</th>
                            <th rowspan="2" class="text-nowrap illuminance">照度</th>
                            <th rowspan="2" class="text-nowrap noise">騒音</th>
                            <th rowspan="2" class="text-nowrap other">{{ measurement_report.other_measure_type }}</th>
                          </tr>
                          <tr>
                            <!-- IES4取込の報告書は乾球／湿球ではなく1.2m／0.1mの2高さで測定している -->
                            <th class="text-nowrap dry-blub">{{ upperTempLabel }}</th>
                            <th class="text-nowrap wet-blub">{{ lowerTempLabel }}</th>
                            <th class="text-nowrap relative-humidity">120cm</th>
                            <th class="text-nowrap co2">瞬間値</th>
                            <th class="text-nowrap co2">平均値</th>
                            <th class="text-nowrap co">瞬間値</th>
                            <th class="text-nowrap co">平均値</th>
                            <th class="text-nowrap floating-dust-amount">瞬間値</th>
                            <th class="text-nowrap floating-dust-amount">平均値</th>
                          </tr>
                          <tr>
                            <th class="text-nowrap time">時：分</th>
                            <th class="text-nowrap person-count">人</th>
                            <th class="text-nowrap person-count">人</th>
                            <th class="text-nowrap dry-blub">℃</th>
                            <th class="text-nowrap wet-blub">℃</th>
                            <th class="text-nowrap relative-humidity">%</th>
                            <th class="text-nowrap air-current">m/sec</th>
                            <th class="text-nowrap co2">ppm</th>
                            <th class="text-nowrap co2">ppm</th>
                            <th class="text-nowrap co">ppm</th>
                            <th class="text-nowrap co">ppm</th>
                            <th class="text-nowrap floating-dust-amount">mg/㎡</th>
                            <th class="text-nowrap floating-dust-amount">mg/㎡</th>
                            <th class="text-nowrap illuminance">Lx</th>
                            <th class="text-nowrap noise">dB</th>
                            <th class="text-nowrap other">{{ measurement_report.other_measure_unit }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="t_measurement_work_place in viewSeparateWorkPlace">
                            <tr v-for="(t_measurement_work_place_content, cIndex) in contentMinimumRows(t_measurement_work_place)" :key="cIndex" :class="{'border-bottom-bold': cIndex === 2}" class="my-3">
                              <td class="work-place" rowspan="3" v-if="cIndex==0">
                                <!-- 階数とポイント -->
                                <div class="d-flex justify-content-center mb-1">
                                  <p class="mb-0">{{ t_measurement_work_place.floor_no }}</p> <!-- 階数 -->
                                  <div v-if="t_measurement_work_place && t_measurement_work_place.point_no">
                                    <p class="mb-0">.P{{ t_measurement_work_place.point_no }}</p> <!-- ポイント -->
                                  </div>
                                  <div v-if="t_measurement_work_place && t_measurement_work_place.outside_air_flg == 1">
                                    <p class="mb-0">外気</p> <!-- 外気 -->
                                  </div>
                                </div>
                                <p class="mb-2">{{ t_measurement_work_place.work_place_nm }}</p> <!-- 測定場所名 -->
                              </td>
                              <td class="text-center time" v-if="t_measurement_work_place_content?.measure_time">{{ formatDateTime(t_measurement_work_place_content?.measure_time) }}</td> <!-- 時間 -->
                              <td class="text-center time" v-if="!t_measurement_work_place_content?.measure_time"><p></p></td> <!-- 時間 -->
                              <td class="text-end person-count">{{ t_measurement_work_place_content?.room_people_num }}</td> <!-- 在室人数 -->
                              <td class="text-end person-count">{{ t_measurement_work_place_content?.smoker_no }}</td> <!-- 喫煙者数 -->
                              <!-- 乾球（IES4取込の場合は1.2m） -->
                              <td class="text-end dry-blub" v-bind:class="{'text-red': (upperTempValue(t_measurement_work_place_content) < tempertureMinStd || upperTempValue(t_measurement_work_place_content) > tempertureMaxStd) && t_measurement_work_place.outside_air_flg == 0}">
                                {{ roundToDecimal(upperTempValue(t_measurement_work_place_content), '', 1) }}
                              </td>
                              <!-- 湿球（IES4取込の場合は0.1m） -->
                              <td class="text-end wet-blub">{{ roundToDecimal(lowerTempValue(t_measurement_work_place_content), '', 1) }}</td>
                              <!-- 相対湿度 -->
                              <td class="text-end relative-humidity" v-bind:class="{'text-red': (t_measurement_work_place_content?.relative_humidity < relativeHumidityMinStd || t_measurement_work_place_content?.relative_humidity > relativeHumidityMaxStd) && t_measurement_work_place.outside_air_flg == 0}">
                                {{ t_measurement_work_place_content?.relative_humidity }}
                              </td>
                              <!-- 気流 -->
                              <td class="text-end air-current" v-bind:class="{'text-red': t_measurement_work_place_content?.air_current > airCurrentMaxStd && t_measurement_work_place.outside_air_flg == 0}">
                                {{ roundToDecimal(t_measurement_work_place_content?.air_current, '', 2) }}
                              </td>
                              <!-- 二酸化炭素瞬間値 -->
                              <td class="text-end co2" v-bind:class="{'text-red': t_measurement_work_place_content?.co2_itt > co2MaxStd && t_measurement_work_place.outside_air_flg == 0}">
                                {{ t_measurement_work_place_content?.co2_itt }}
                              </td>
                              <!-- 二酸化炭素平均値 -->
                              <td class="text-end co2" v-bind:class="{'text-red': roundToSecondDecimal(t_measurement_work_place.co2_avg) > co2MaxStd && t_measurement_work_place.outside_air_flg == 0}" rowspan="3" v-if="cIndex==0">
                                {{ roundToSecondDecimal(t_measurement_work_place.co2_avg) }}
                              </td>
                              <!-- 一酸化炭素瞬間値 -->
                              <td class="text-end co" v-bind:class="{'text-red': t_measurement_work_place_content?.co_itt > coMaxStd && t_measurement_work_place.outside_air_flg == 0}">
                                {{ roundToDecimal(t_measurement_work_place_content?.co_itt, '', 1) }}
                              </td>
                              <!-- 一酸化炭素平均値 -->
                              <td class="text-end co" v-bind:class="{'text-red': roundToDecimal(t_measurement_work_place.co_avg, '', 1) > coMaxStd && t_measurement_work_place.outside_air_flg == 0}" rowspan="3" v-if="cIndex==0">
                                {{ roundToDecimal(t_measurement_work_place.co_avg, '', 1) }}
                              </td>
                              <!-- 浮遊粉塵量間値 -->
                              <td class="text-end floating-dust-amount" v-bind:class="{'text-red': t_measurement_work_place_content?.float_powder_dust_itt > floatPowderDustMaxStd && t_measurement_work_place.outside_air_flg == 0}">
                                {{ roundToDecimal(t_measurement_work_place_content?.float_powder_dust_itt, '', 3) }}
                              </td>
                              <!-- 浮遊粉塵量平均値 -->
                              <td class="text-end floating-dust-amount" v-bind:class="{'text-red': roundToDecimal(t_measurement_work_place.float_powder_dust_avg, '', 3) > floatPowderDustMaxStd && t_measurement_work_place.outside_air_flg == 0}" rowspan="3" v-if="cIndex==0">
                                {{ roundToDecimal(t_measurement_work_place.float_powder_dust_avg, '', 3) }}
                              </td>
                              <td 
                                :class="{
                                  'text-end illuminance': t_measurement_work_place_content?.illuminance !== 0,
                                  'text-center illuminance': t_measurement_work_place_content?.illuminance === 0
                                }"
                              >
                                {{ t_measurement_work_place_content?.illuminance === 0 ? 'ー' : t_measurement_work_place_content?.illuminance }}
                              </td> <!-- 照度 -->
                              <td class="text-end noise">{{ t_measurement_work_place_content?.noise }}</td> <!-- 騒音 -->
                              <td class="text-end other">{{ roundToDecimal(t_measurement_work_place_content?.formaldehyde, '', 2) }}</td> <!-- ホルムアルデヒド -->
                              <td class="text-start remarks">{{ t_measurement_work_place_content?.remarks }}</td> <!-- 備考 -->
                            </tr>
                          </template>
                          <tr v-if="idx === viewSeparateWorkPlaces.length - 1">
                            <td colspan="4">使用測定機器</td>
                            <td colspan="2">{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 1)?.device_nm }}</td>
                            <td>{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 2)?.device_nm }}</td>
                            <td>{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 3)?.device_nm }}</td>
                            <td colspan="2">{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 4)?.device_nm }}</td>
                            <td colspan="2">{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 5)?.device_nm }}</td>
                            <td colspan="2">{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 6)?.device_nm }}</td>
                            <td>{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 8)?.device_nm }}</td>
                            <td>{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 7)?.device_nm }}</td>
                            <td>{{ measurement_report.t_measurement_used_devices.find(device => device.m_measure_type_id === 9)?.device_nm }}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <!-- 環境測定結果 -->
              </div>

              <div v-else class="text-center">
                建物情報を読み込み中...
              </div>

            </div>
            <div v-else class="text-center">
              顧客情報を読み込み中...
            </div>

            <!-- 温熱条件測定結果 -->
            <div v-for="n in SeparateWorkPlaceCount" :key="n" class="thermal-condition-results">
              <h2 class="text-center">温熱条件測定結果</h2>
              <div class="precedent d-flex justify-content-end">
                <table>
                  <thead>
                    <tr>
                      <th>記号</th>
                      <th>測定日</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div class="graph-symbol d-flex align-items-center justify-content-between">
                          <div class="circle"></div>
                          <div class="line"></div>
                          <div class="circle"></div>
                          <div class="line"></div>
                          <div class="circle"></div>
                        </div>
                        <div class="graph-symbol-tip d-flex align-items-center justify-content-between">
                          <p>1回目</p>
                          <p>2回目</p>
                          <p>3回目</p>
                        </div>
                      </td>
                      <td>{{ formatDatePattern3(measurement_report.measure_dt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="result-area">
                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>温度</h3>
                    <div class="canvas-parent">
                      <canvas :height="'180px'" :width="canvasWidth + 'px'" :id="`tempertureChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideTempertureChartCanvas`"></canvas>
                    </div>
                  </div>
                </div>

                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>相対湿度</h3>
                    <div class="canvas-parent">
                      <canvas :height="'180px'" :width="canvasWidth + 'px'" :id="`relativeHumidityChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideRelativeHumidityChartCanvas`"></canvas>
                    </div>
                  </div>
                </div>

                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>気流</h3>
                    <div class="canvas-parent">
                      <canvas :height="'180px'" :width="canvasWidth + 'px'" :id="`airCurrentChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type not-applicable-area">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideAirCurrentChartCanvas`"></canvas>
                    </div>
                    <div class="not-applicable-overlay" v-if="n === 1">
                      測定対象外
                    </div>
                  </div>
                </div>
              </div>

              <p class="outside-mark" v-if="n === 1">外気</p>
            </div>
            <!-- 温熱条件測定結果 -->

            <!-- 空気清浄度測定結果 -->
            <div v-for="n in SeparateWorkPlaceCount" :key="n" class="thermal-condition-results">
              <h2 class="text-center">空気清浄度測定結果</h2>
              <div class="precedent d-flex justify-content-end">
                <table>
                  <thead>
                    <tr>
                      <th colspan="2">記号</th>
                      <th>測定日</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>瞬間値</td>
                      <td>
                        <div class="graph-symbol d-flex align-items-center justify-content-between">
                          <div class="circle"></div>
                          <div class="line"></div>
                          <div class="circle"></div>
                          <div class="line"></div>
                          <div class="circle"></div>
                        </div>
                        <div class="graph-symbol-tip d-flex align-items-center justify-content-between">
                          <p>1回目</p>
                          <p>2回目</p>
                          <p>3回目</p>
                        </div>
                      </td>
                      <td>{{ formatDatePattern3(measurement_report.measure_dt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="result-area">
                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>二酸化炭素</h3>
                    <div class="canvas-parent">
                      <canvas height="180px" :width="canvasWidth + 'px'" :id="`co2ChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideCo2ChartCanvas`"></canvas>
                    </div>
                  </div>
                </div>

                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>一酸化炭素</h3>
                    <div class="canvas-parent">
                      <canvas height="180px" :width="canvasWidth + 'px'" :id="`coChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideCoChartCanvas`"></canvas>
                    </div>
                  </div>
                </div>
                
                <div class="measure-unit d-flex justify-content-between">
                  <div class="result-type d-flex aling-items-center">
                    <h3>浮遊粉塵量</h3>
                    <div class="canvas-parent">
                      <canvas height="180px" :width="canvasWidth + 'px'" :id="`floatPowderDustChartCanvas${n}`"></canvas>
                    </div>
                  </div>
                  <div class="out-enter-type">
                    <div class="canvas-parent" v-if="n === 1">
                      <canvas :height="'180px'" :width="outsideCanvasWidth + 'px'" :id="`outsideFloatPowderDustChartCanvas`"></canvas>
                    </div>
                  </div>
                </div>
              </div>

              <p class="outside-mark" v-if="n === 1">外気</p>
            </div>
            <!-- 空気清浄度測定結果 -->

          </div>
          <div v-else class="text-center">
            測定報告書情報を読み込み中...
          </div>
        </div>

        <!-- 社内向け備考 -->
        <div v-if="measurement_report.m_building" class="px-4 pb-5">
          <div v-if="measurement_report.internal_remarks">
            <h6 class="text-center"><strong>社内向け備考</strong><span class="text-danger">※報告書には表示されません</span></h6>
            <div class="text-center" v-bind:style="{ 'white-space': 'pre-line' }">
              {{ measurement_report.internal_remarks }}
            </div>
          </div>
        </div>
        <!-- 社内向け備考 -->

        <div v-if="measurement_report && measurement_report.m_customer_id && measurement_report.m_building_id" class="btn-area">
          <router-link :to="{ name: 'CustomerBuildingReportList', params: { customer_id: measurement_report.m_customer_id, building_id: measurement_report.m_building_id }}">
            <button type="button" class="btn btn-primary mx-1">報告書一覧</button>
          </router-link>

          <router-link :to="{ name: 'CustomerMeasurementReportEdit', params: { id: measurement_report.id, customer_id: measurement_report.m_customer_id, building_id: measurement_report.m_building_id }}">
            <button type="button" class="btn btn-success mx-2">編集</button>
          </router-link>

          <button class="btn btn-info" @click="generatePDF">PDFを出力</button>
        </div>

        <div v-if="measurement_report && measurement_report.m_customer_id && measurement_report.m_building_id" class="btn-area-fixed">
          <router-link :to="{ name: 'CustomerBuildingReportList', params: { customer_id: measurement_report.m_customer_id, building_id: measurement_report.m_building_id }}">
            <button type="button" class="btn btn-primary mx-1">報告書一覧</button>
          </router-link>

          <router-link :to="{ name: 'CustomerMeasurementReportEdit', params: { id: measurement_report.id, customer_id: measurement_report.m_customer_id, building_id: measurement_report.m_building_id }}">
            <button type="button" class="btn btn-success mx-2">編集</button>
          </router-link>

          <button class="btn btn-info" @click="generatePDF">PDFを出力</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { createApp, ref, nextTick, onMounted, watchEffect, computed } from 'vue';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);
import html2pdf from 'html2pdf.js'
import { useRoute } from 'vue-router';

// import { createApp } from 'vue';
import MeasurementReportDetailPDF from './MeasurementReportDetailPDF.vue';
import { getMeasurementReport, getMeasureStds } from '@/api/axios';
// import { getMeasurementReport, getMeasureStds, generateMesurementReportPDF } from '@/api/axios';

export default {
  setup() {
    const title = ref('測定報告書詳細');

    watchEffect(() => {
      document.title = title.value;
    });
    
    const measurement_report = ref({});
    const viewSeparateWorkPlaces = ref([]); // 画面レイアウトで使う作業場所の多重配列
    const viewSeparateWorkPlaceCount = ref();
    const measure_stds = ref([]);
    const route = useRoute();
    const isLoading = ref(false);  // ローディング状態の追加
    const measurement_reportId = route.params.id;
    const tempertureMinStd = ref(); // 温度の基準値（最低）
    const tempertureMaxStd = ref(); // 温度の基準値（最大）
    const relativeHumidityMinStd = ref(); // 相対湿度の基準値（最低）
    const relativeHumidityMaxStd = ref(); // 相対湿度の基準値（最大）
    const airCurrentMaxStd = ref(); // 気流の基準値（最大）
    const co2MaxStd = ref(); // 二酸化炭素の基準値（最大）
    const coMaxStd = ref(); // 一酸化炭素の基準値（最大）
    const floatPowderDustMaxStd = ref(); // 浮遊粉塵量の基準値（最大）

    // グラフ要素
    const tempertureChartCanvases = ref([]); // 温度のキャンバス
    const relativeHumidityChartCanvases = ref([]); // 相対湿度のキャンバス
    const airCurrentChartCanvases = ref([]); // 気流のキャンバス
    const co2ChartCanvases = ref([]); // 二酸化炭素のキャンバス
    const coChartCanvases = ref([]); // 一酸化炭素のキャンバス
    const floatPowderDustChartCanvases = ref([]); // 浮遊粉塵量のキャンバス

    const outsideTempertureChartCanvas = ref(); // 温度のキャンバス（外気）
    const outsideRelativeHumidityChartCanvas = ref(); // 相対湿度のキャンバス（外気）
    const outsideAirCurrentChartCanvas = ref(); // 気流のキャンバス（外気）
    const outsideCo2ChartCanvas = ref(); // 二酸化炭素のキャンバス（外気）
    const outsideCoChartCanvas = ref(); // 一酸化炭素のキャンバス（外気）
    const outsideFloatPowderDustChartCanvas = ref(); // 浮遊粉塵量のキャンバス（外気）
    
    const tempertureChartData = ref([]); // 温度のグラフデータ
    const relativeHumidityChartData = ref([]); // 相対湿度のグラフデータ
    const airCurrentChartData = ref([]); // 気流のグラフデータ
    const co2ChartData = ref([]); // 二酸化炭素のグラフデータ
    const coChartData = ref([]); // 一酸化炭素のグラフデータ
    const floatPowderDustChartData = ref([]); // 浮遊粉塵量のグラフデータ
    
    const outsideTempertureChartData = ref(); // 温度のグラフデータ（外気）
    const outsideRelativeHumidityChartData = ref(); // 相対湿度のグラフデータ（外気）
    const outsideAirCurrentChartData = ref(); // 気流のグラフデータ（外気）
    const outsideCo2ChartData = ref(); // 二酸化炭素のグラフデータ（外気）
    const outsideCoChartData = ref(); // 一酸化炭素のグラフデータ（外気）
    const outsideFloatPowderDustChartData = ref(); // 浮遊粉塵量のグラフデータ（外気）

    const tempertureMax = ref(); // 温度の最大値（室内）
    const airCurrentMax = ref(); // 気流の最大値（室内）
    const co2Max = ref(-Infinity); // 二酸化炭素の最大値（室内）
    const coMax = ref(-Infinity); // 一酸化炭素の最大値（室内）
    const floatPowderDustMax = ref(-Infinity); // 浮遊粉塵量の最大値（室内）

    const workPlaces = ref([]); // 室内用作業場所
    const insideAirWorkPlaces = ref([]); // 室内の作業場所
    const outWorkPlaces = ref([]); // 外気用作業場所
    const outsideAirWorkPlaces = ref([]); // 外気の作業場所
    const viewOutsideAirWorkPlaces = ref([]); // View用外気の作業場所
    const SeparateWorkPlaces = ref([]); // 室内の作業場所の14箇所区切り
    const SeparateWorkPlaceCount = ref(null); // 14箇所区切りにされた作業場所の数
    
    const tempertures = ref([]); // 作業場所の測定温度の値
    const relativeHumidities = ref([]); // 作業場所の測定相対湿度の値
    const airCurrents = ref([]); // 作業場所の測定気流の値
    const lineCo2s = ref([]); // 作業場所の測定二酸化炭素の値（折れ線グラフタイプ）
    const barCo2s = ref([]); // 作業場所の測定二酸化炭素の値（棒グラフタイプ）
    const lineCos = ref([]); // 作業場所の測定一酸化炭素の値（折れ線グラフタイプ）
    const barCos = ref([]); // 作業場所の測定一酸化炭素の値（棒グラフタイプ）
    const lineFloatPowderDusts = ref([]); // 作業場所の測定浮遊粉塵量の値（折れ線グラフタイプ）
    const barFloatPowderDusts = ref([]); // 作業場所の測定浮遊粉塵量の値（棒グラフタイプ）
    
    const outsideTempertures = ref([]); // 作業場所の測定温度の値（外気）
    const outsideRelativeHumidities = ref([]); // 作業場所の測定相対湿度の値（外気）
    const outsideAirCurrents = ref([]); // 作業場所の測定気流の値（外気）
    const outsideLineCo2s = ref([]); // 作業場所の測定二酸化炭素の値（折れ線グラフタイプ）（外気）
    const outsideBarCo2s = ref([]); // 作業場所の測定二酸化炭素の値（棒グラフタイプ）（外気）
    const outsideLineCos = ref([]); // 作業場所の測定一酸化炭素の値（折れ線グラフタイプ）（外気）
    const outsideBarCos = ref([]); // 作業場所の測定一酸化炭素の値（棒グラフタイプ）（外気）
    const outsideLineFloatPowderDusts = ref([]); // 作業場所の測定浮遊粉塵量の値（折れ線グラフタイプ）（外気）
    const outsideBarFloatPowderDusts = ref([]); // 作業場所の測定浮遊粉塵量の値（棒グラフタイプ）（外気）
    
    const chartInstances = ref([]); // キャンバスのグラフの配列（室内）
    const canvasWidth = ref(0); // 室内のグラフの横幅
    const outsideChartInstances = ref([]); // キャンバスのグラフの配列（外気）
    const outsideCanvasWidth = ref(0); // 外気のグラフの横幅

    const fetchMeasurementReport = async () => {
      try {
        const response = await getMeasureStds();
        measure_stds.value = response.data;
        tempertureMinStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 1 && measure_std.min_max_type === 0)?.std_val;
        tempertureMaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 1 && measure_std.min_max_type === 1)?.std_val;
        relativeHumidityMinStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 2 && measure_std.min_max_type === 0)?.std_val;
        relativeHumidityMaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 2 && measure_std.min_max_type === 1)?.std_val;
        airCurrentMaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 3 && measure_std.min_max_type === 1)?.std_val;
        co2MaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 4 && measure_std.min_max_type === 1)?.std_val;
        coMaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 5 && measure_std.min_max_type === 1)?.std_val;
        floatPowderDustMaxStd.value = measure_stds.value.find(measure_std => measure_std.m_measure_type_id === 6 && measure_std.min_max_type === 1)?.std_val;
      } catch (error) {
        console.error('空気環境測定基準値情報ーデータの取得に失敗しました:', error);
      }

      try {
        const response = await getMeasurementReport(measurement_reportId);
        measurement_report.value = response.data;
        insideAirWorkPlaces.value = measurement_report.value.t_measurement_work_places.filter(place => place.outside_air_flg === 0);
        outsideAirWorkPlaces.value = measurement_report.value.t_measurement_work_places.filter(place => place.outside_air_flg === 1);
        viewOutsideAirWorkPlaces.value = measurement_report.value.t_measurement_work_places.filter(place => place.outside_air_flg === 1);

        viewSeparateWorkPlaces.value = chunkArrayCustom(insideAirWorkPlaces.value, viewOutsideAirWorkPlaces.value);
        viewSeparateWorkPlaceCount.value = viewSeparateWorkPlaces.value.length;

        SeparateWorkPlaces.value = chunkArray(insideAirWorkPlaces.value, 14);
        SeparateWorkPlaceCount.value = SeparateWorkPlaces.value.length;

        // グラフ用の最大値のセット
        tempertureMax.value = measurement_report.value.t_measure_result.temperture_max_measure
        airCurrentMax.value = measurement_report.value.t_measure_result.air_current_max_measure

        insideAirWorkPlaces.value.forEach((workPlace) => {
          workPlace.t_measurement_work_place_contents.forEach((content) => {
            co2Max.value = Math.max(co2Max.value, content.co2_itt);
            coMax.value = Math.max(coMax.value, content.co_itt);
            floatPowderDustMax.value = Math.max(floatPowderDustMax.value, content.float_powder_dust_itt);
          });
        });

        updateChartData();
      } catch (error) {
        console.error('測定報告書のデータ取得に失敗しました:', error);
      }
    };

    const workPlaceMinimumRows = computed(() => {
      // const minRows = 30;
      const rows = insideAirWorkPlaces.value.slice(); // devices配列のシャローコピーを作成
      // while (rows.length < minRows) {
      //   rows.push(null); // 最低行数を満たすためにnullを追加
      // }
      return rows;
    });

    // 明細は測定回数が2回の報告書もあるため、表示は常に3行に揃える（不足分は空行）
    const CONTENT_DISPLAY_ROWS = 3;
    const contentMinimumRows = (workPlace) => {
      const rows = (workPlace?.t_measurement_work_place_contents || []).slice();
      while (rows.length < CONTENT_DISPLAY_ROWS) {
        rows.push(null); // 最低行数を満たすためにnullを追加
      }
      return rows;
    };

    // IES4取込の報告書は乾球／湿球ではなく1.2m（TempU）／0.1m（TempL）の2高さで温度を測定するため、
    // 見出しと参照する値を切り替える。手入力で作成した報告書は従来どおり乾球／湿球。
    const isIes4Imported = computed(() => measurement_report.value.ies4_imported === true);
    const upperTempLabel = computed(() => (isIes4Imported.value ? '1.2m' : '乾球'));
    const lowerTempLabel = computed(() => (isIes4Imported.value ? '0.1m' : '湿球'));
    const upperTempValue = (content) => (
      isIes4Imported.value ? content?.temperature_upper : content?.dry_bulb
    );
    const lowerTempValue = (content) => (
      isIes4Imported.value ? content?.temperature_lower : content?.wet_bulb
    );

    const deviceMinimumRows = computed(() => {
      const minRows = 9;
      const rows = measurement_report.value.t_measurement_used_devices.slice(); // devices配列のシャローコピーを作成
      while (rows.length < minRows) {
        rows.push(null); // 最低行数を満たすためにnullを追加
      }
      return rows;
    });

    // view用に作業場所を区切る関数
    const chunkArrayCustom = (insideWorkPlace, outsideWorkPlace) => {
      let result = [];
      let chunkSize = 9; // 最初のチャンクサイズを9に設定

      const workPlaceAjustValue = {
        work_place_nm: '',
        floor_no: '',
        point_no: '',
        sort_no: '',
        outside_air_flg: 0, // これが外気を示すフラグ
        co2_avg: '', 
        co_avg: '', 
        float_powder_dust_avg: '', 
        t_measurement_work_place_contents: [
          { measurement_count: 1, co2_itt: '', co_itt: '', float_powder_dust_itt: '' },
          { measurement_count: 2, co2_itt: '', co_itt: '', float_powder_dust_itt: '' },
          { measurement_count: 3, co2_itt: '', co_itt: '', float_powder_dust_itt: '' }
        ]
      }

      // 室内が全くない場合
      if ( insideWorkPlace.length === 0 ) {
        result.push(outsideWorkPlace)
        for (let i = 0; i < 7; i += 1) {
          result[result.length - 1].unshift(workPlaceAjustValue);
        }
        return result
      }

      for (let i = 0; i < insideWorkPlace.length; i += chunkSize) {
        if (chunkSize === 9 && i > 0) { // 最初のループ後にチャンクサイズを10に変更
          chunkSize = 10;
        }
        result.push(insideWorkPlace.slice(i, i + chunkSize));
      }

      // 室内は1ページで収まるが外気が次ページになる場合
      if (result.length === 1 && result[result.length - 1].length > 7) {
        for (let i = result[result.length - 1].length; i < 9; i += 1) {
          result[result.length - 1].push(workPlaceAjustValue);
        }

        result.push(outsideWorkPlace)

        for (let i = 0; i < 7; i += 1) {
          result[result.length - 1].unshift(workPlaceAjustValue);
        }

      // 室内が2ページ以上でさらに外気が次ページになる場合
      } else if (result.length > 1 && result[result.length - 1].length > 8) {
        for (let i = result[result.length - 1].length; i < 10; i += 1) {
          result[result.length - 1].push(workPlaceAjustValue);
        }

        result.push(outsideWorkPlace)

        for (let i = 0; i < 8; i += 1) {
          result[result.length - 1].unshift(workPlaceAjustValue);
        }

      // 室内も外気も1ページで収まる場合
      } else if (result.length === 1 && result[result.length - 1].length <= 7) {
        for (let i = result[result.length - 1].length; i < 7; i += 1) {
          result[result.length - 1].push(workPlaceAjustValue);
        }
        result[result.length - 1] = result[result.length - 1].concat(outsideWorkPlace);

      // 室内が2ページ以上だが、外気で次ページに行かない場合
      } else {
        for (let i = result[result.length - 1].length; i < 8; i += 1) {
          result[result.length - 1].push(workPlaceAjustValue);
        }
        result[result.length - 1] = result[result.length - 1].concat(outsideWorkPlace);
      }
      return result;
    };

    // 配列を14個の要素ごとに区切る関数
    const chunkArray = (array, chunkSize) => {
      let result = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
      }
      return result;
    };

    function createChartOptions(xType, yBool, max, yMin, yMax, stepSize, workPlaces, unit) {
      return {
        plugins: {
          legend: {
            display: false // 判例を非表示にする
        }
        },
        scales: {
          x: {
            type: xType, // 数値軸を指定
            min: 0, // X軸の最小値
            max: max, // X軸の最大値,
            offset: false,
            grid: {
              display: true, // x軸のグリッド線を表示
              drawBorder: true, // グラフの外枠のボーダーを表示
              drawOnChartArea: true, // チャート領域にグリッド線を描画
            },
            ticks: {
              display: false,
              stepSize: stepSize, // メモリ間の固定間隔
              autoSkip: false, // すべてのメモリを表示
              maxRotation: 0, // ラベルを水平に保持
              minRotation: 0,
              align: 'center',
              // ティックのラベルをカスタマイズ
              callback: function(value, index) {
                // ここで動的にworkPlacesからラベルを設定
                var label = workPlaces[index];
                return label
              }
            }
          },
          y: {
            beginAtZero: yBool,
            min: yMin,
            max: yMax,
            // Y軸のタイトルを追加
            title: {
              display: yBool, // タイトルを表示する
              text: `(${unit})`, // 表示するテキスト（ここに単位を入れます）
              align: 'end', // タイトルの位置を調整（'start', 'end', 'center'が指定可能）
              color: '#666', // タイトルの色
              font: {
                size: 14, // フォントサイズ
                weight: 'normal' // フォントの太さ
              }
            },
             ticks: {
              display: yBool // y軸の目盛りラベルを非表示にします
            },
             grid: {
              display: yBool, // y軸のグリッド線を表示
              drawBorder: yBool, // グラフの外枠のボーダーを表示
              drawOnChartArea: yBool, // チャート領域にグリッド線を描画
            }
          },
        },
        maintainAspectRatio: false
      }
    }

    //  右側に余白を追加する関数
    function setChartPadding(options, paddingLeftValue, paddingRightValue) {
      options.layout = {
        padding: {
          left: paddingLeftValue,
          right: paddingRightValue, // 右側に余白を追加
          bottom: 60
        }
      };
    }

    function drawTemperatureLabels(chartInstance, minStd, maxStd, unit) {
      const ctx = chartInstance.ctx;
      const yAxis = chartInstance.scales.y;
      const xEnd = chartInstance.chartArea.right; // チャートの右端

      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'red';

      if (minStd) {
      // 最低基準のラベルを描画
        const yMinPixel = yAxis.getPixelForValue(minStd);
        ctx.fillText(`${minStd}${unit}`, xEnd + 10, yMinPixel); // チャートの右端から10ピクセル右にずらす
      }

      if (maxStd) {
        // 最高基準のラベルを描画
        const yMaxPixel = yAxis.getPixelForValue(maxStd);
        ctx.fillText(`${maxStd}${unit}`, xEnd + 10, yMaxPixel);
      }
    }

    // ラベル改行のためのプラグインを定義
    const breakLabelsPlugin = {
      id: 'breakLabelsPlugin', // プラグインの一意なID
      afterDraw: function(chartInstance) {
        var ctx = chartInstance.ctx;
        var chartId = chartInstance.canvas.id;
        var xAxis = chartInstance.scales.x; // 'x' は Chart.js のバージョンによって異なる可能性がある
        var yAxis = chartInstance.scales.y;

        var fontSize = 9; // フォントサイズを指定
        var fontFamily = 'sans-serif'; // お好みでフォントファミリを指定
        var fontWeight = 'bold'; // 太文字に設定
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = 'black'; // テキストの色を黒に設定

        xAxis.ticks.forEach(function(value, index) {
          var x = xAxis.getPixelForTick(index);
          if (chartId.includes('temperture') || chartId.includes('relativeHumidity') || chartId.includes('airCurrent')) {
            x += 20; // ラベルを右に20ピクセル移動
          } else if (chartId.includes('outsideTemperture') || chartId.includes('outsideRelativeHumidity') || chartId.includes('outsideAirCurrent')) {
            x += 24; // ラベルを右に24ピクセル移動
          }

          var y = yAxis.bottom + 20; // ラベルの開始位置を Y軸の下に設定

          // var label = chartInstance.data.labels[index];
          var label = xAxis.ticks[index].label;
          if (label) {
            var words = label.match(/.{1,5}/g) || [];
            var lineHeight = 15; // ラインの高さを設定

            // ここで改行を適用しながらラベルを描画
            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            words.forEach(function(word, line) {
              ctx.fillText(word, x, y + (line * lineHeight));
            });
            ctx.restore();
          }
        });
      }
    };

    // ここでプラグインをChart.jsに登録
    Chart.register(breakLabelsPlugin);

    // ====================== 外気グラフ生成 ======================
    const outsideDrawCharts = () => {
      outsideChartInstances.value = [];

      // ============ 温度（外気）============
      const outsideTempertureCanvasId = `outsideTempertureChartCanvas`;
      const outsideTempertureChartDatasetVal = outsideTempertureChartData.value;
      const outsideTempertureCtx = document.getElementById(outsideTempertureCanvasId).getContext('2d');
      const outsideTempertureOptions = createChartOptions('linear', true, 6, -5, 40, 3, outsideTempertureChartDatasetVal.workPlaces, outsideTempertureChartDatasetVal.unit, true);

      // チャートのパディングを設定
      setChartPadding(outsideTempertureOptions, 0, 80);

      const outsideTempertureChartInstance = new Chart(outsideTempertureCtx, {
        type: 'line',
        data: outsideTempertureChartDatasetVal,
        options: outsideTempertureOptions,
        plugins: ['xLabelCenter']
      });

      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideTempertureChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideTempertureChartInstance, null, null, outsideTempertureChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideTempertureCanvasId, instance: outsideTempertureChartInstance });
      // ============ 温度（外気）============

      // ============ 相対湿度（外気）============
      const outsideRelativeHumidityCanvasId = `outsideRelativeHumidityChartCanvas`;
      const outsideRelativeHumidityChartDatasetVal = outsideRelativeHumidityChartData.value;
      const outsideRelativeHumidityCtx = document.getElementById(outsideRelativeHumidityCanvasId).getContext('2d');
      const outsideRelativeHumidityOptions = createChartOptions('linear', true, 6, 0, 100, 3, outsideRelativeHumidityChartDatasetVal.workPlaces, outsideRelativeHumidityChartDatasetVal.unit);

      // チャートのパディングを設定
      setChartPadding(outsideRelativeHumidityOptions, 0, 80);

      const outsideRelativeHumidityChartInstance = new Chart(outsideRelativeHumidityCtx, {
        type: 'line',
        data: outsideRelativeHumidityChartDatasetVal,
        options: outsideRelativeHumidityOptions,

      });
      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideRelativeHumidityChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideRelativeHumidityChartInstance, null, null, outsideRelativeHumidityChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideRelativeHumidityCanvasId, instance: outsideRelativeHumidityChartInstance });
      // ============ 相対湿度（外気）============

      // ============ 気流（外気）============
      const outsideAirCurrentCanvasId = `outsideAirCurrentChartCanvas`;
      const outsideAirCurrentChartDatasetVal = outsideAirCurrentChartData.value;
      const outsideAirCurrentCtx = document.getElementById(outsideAirCurrentCanvasId).getContext('2d');
      const outsideAirCurrentOptions = createChartOptions('linear', false, 6, null, null, 3, outsideAirCurrentChartDatasetVal.workPlaces, outsideAirCurrentChartDatasetVal.unit);

      // チャートのパディングを設定
      setChartPadding(outsideAirCurrentOptions, 40, 80);

      const outsideAirCurrentChartInstance = new Chart(outsideAirCurrentCtx, {
        type: 'line',
        data: outsideAirCurrentChartDatasetVal,
        options: outsideAirCurrentOptions,
      });

      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideAirCurrentChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideAirCurrentChartInstance, null, null, outsideAirCurrentChartDatasetVal.unit);
        // drawTemperatureLabels(outsideAirCurrentChartInstance, null, airCurrentMaxStd.value, outsideAirCurrentChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideTempertureCanvasId, instance: outsideTempertureChartInstance });
      // ============ 気流（外気）============

      // ============ 二酸化炭素（外気）============
      const outsideCo2CanvasId = `outsideCo2ChartCanvas`;
      const outsideCo2ChartDatasetVal = outsideCo2ChartData.value;
      const outsideCo2Ctx = document.getElementById(outsideCo2CanvasId).getContext('2d');
      const outsideCo2Options = createChartOptions('category', true, 8, 0, 1000, 4, outsideCo2ChartDatasetVal.workPlaces, outsideCo2ChartDatasetVal.unit);

      // チャートのパディングを設定
      setChartPadding(outsideCo2Options, 0, 80);

      const outsideCo2ChartInstance = new Chart(outsideCo2Ctx, {
        type: 'line',
        data: outsideCo2ChartDatasetVal,
        options: outsideCo2Options,
      });

      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideCo2ChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideCo2ChartInstance, null, null, outsideCo2ChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideCo2CanvasId, instance: outsideCo2ChartInstance });
      // ============ 二酸化炭素（外気）============

      // ============ 一酸化炭素（外気）============
      const outsideCoCanvasId = `outsideCoChartCanvas`;
      const outsideCoChartDatasetVal = outsideCoChartData.value;
      const outsideCoCtx = document.getElementById(outsideCoCanvasId).getContext('2d');
      const outsideCoOptions = createChartOptions('category', true, 8, 0, 10, 4, outsideCoChartDatasetVal.workPlaces, outsideCoChartDatasetVal.unit);

      // チャートのパディングを設定
      setChartPadding(outsideCoOptions, 0, 80);

      const outsideCoChartInstance = new Chart(outsideCoCtx, {
        type: 'line',
        data: outsideCoChartDatasetVal,
        options: outsideCoOptions,
      });

      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideCoChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideCoChartInstance, null, null, outsideCoChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideCoCanvasId, instance: outsideCoChartInstance });
      // ============ 一酸化炭素（外気）============

      // ============ 浮遊粉塵量（外気）============
      const outsideFloatPowderDustCanvasId = `outsideFloatPowderDustChartCanvas`;
      const outsideFloatPowderDustChartDatasetVal = outsideFloatPowderDustChartData.value;
      const outsideFloatPowderDustCtx = document.getElementById(outsideFloatPowderDustCanvasId).getContext('2d');
      const outsideFloatPowderDustOptions = createChartOptions('category', true, 8, 0, 0.2, 4, outsideFloatPowderDustChartDatasetVal.workPlaces, outsideFloatPowderDustChartDatasetVal.unit);

      // チャートのパディングを設定
      setChartPadding(outsideFloatPowderDustOptions, 0, 80);

      const outsideFloatPowderDustChartInstance = new Chart(outsideFloatPowderDustCtx, {
        type: 'line',
        data: outsideFloatPowderDustChartDatasetVal,
        options: outsideFloatPowderDustOptions,
      });

      // アニメーションが完了したら、ラベルを描画する関数を設定
      outsideFloatPowderDustChartInstance.options.animation.onComplete = function() {
        drawTemperatureLabels(outsideFloatPowderDustChartInstance, null, null, outsideFloatPowderDustChartDatasetVal.unit);
      };

      outsideChartInstances.value.push({ id: outsideFloatPowderDustCanvasId, instance: outsideFloatPowderDustChartInstance });
      // ============ 浮遊粉塵量（外気）============
    };
    // ====================== 外気グラフ生成 ======================

    // ====================== 室内グラフ生成 ======================
    const drawCharts = () => {
      chartInstances.value = [];

      tempertureChartData.value.forEach((dataset, index) => {
        const canvasId = `tempertureChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        tempertureMax.value = (tempertureMax.value > 35) ? tempertureMax.value : 35;
        const options = createChartOptions('linear', true, 42, 0, tempertureMax.value, 3, dataset.workPlaces, dataset.unit, true);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, tempertureMinStd.value, tempertureMaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });

      relativeHumidityChartData.value.forEach((dataset, index) => {
        const canvasId = `relativeHumidityChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        const options = createChartOptions('linear', true, 42, 0, 100, 3, dataset.workPlaces, dataset.unit);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, relativeHumidityMinStd.value, relativeHumidityMaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });

      airCurrentChartData.value.forEach((dataset, index) => {
        const canvasId = `airCurrentChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        airCurrentMax.value = (airCurrentMax.value > airCurrentMaxStd.value) ? airCurrentMax.value : airCurrentMaxStd.value;
        const options = createChartOptions('linear', true, 42, 0, airCurrentMax.value, 3, dataset.workPlaces, dataset.unit);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, null, airCurrentMaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });

      co2ChartData.value.forEach((dataset, index) => {
        const canvasId = `co2ChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        co2Max.value = (co2Max.value > co2MaxStd.value) ? co2Max.value : co2MaxStd.value;
        const options = createChartOptions('category', true, 56, 0, co2Max.value, 4, dataset.workPlaces, dataset.unit);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, null, co2MaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });

      coChartData.value.forEach((dataset, index) => {
        const canvasId = `coChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        coMax.value = (coMax.value > coMaxStd.value) ? coMax.value : coMaxStd.value;
        const options = createChartOptions('category', true, 56, 0, coMax.value, 4, dataset.workPlaces, dataset.unit);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, null, coMaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });

      floatPowderDustChartData.value.forEach((dataset, index) => {
        const canvasId = `floatPowderDustChartCanvas${index + 1}`;
        const ctx = document.getElementById(canvasId).getContext('2d');
        floatPowderDustMax.value = (floatPowderDustMax.value > floatPowderDustMaxStd.value) ? floatPowderDustMax.value : floatPowderDustMaxStd.value;
        const options = createChartOptions('category', true, 56, 0, floatPowderDustMax.value, 4, dataset.workPlaces, dataset.unit);

        // チャートのパディングを設定
        setChartPadding(options, 0, 80);

        const chartInstance = new Chart(ctx, {
          type: 'line',
          data: dataset,
          options: options,
        });

        // アニメーションが完了したら、ラベルを描画する関数を設定
        chartInstance.options.animation.onComplete = function() {
          drawTemperatureLabels(chartInstance, null, floatPowderDustMaxStd.value, dataset.unit);
        };

        // インスタンスを配列に保存
        chartInstances.value.push({ id: canvasId, instance: chartInstance });
      });
    };
    // ====================== 室内グラフ生成 ======================

    const outsideUpdateChartSize = (canvasId, newWidth, newHeight) => {
      const chartInfo = outsideChartInstances.value.find(info => info.id === canvasId);
      if (chartInfo) {
        const canvas = chartInfo.instance.canvas;
        canvas.style.width = `${newWidth}px`;
        canvas.style.height = `${newHeight}px`;
        chartInfo.instance.update();
      }
    };

    const updateChartSize = (canvasId, newWidth, newHeight) => {
      const chartInfo = chartInstances.value.find(info => info.id === canvasId);
      if (chartInfo) {
        const canvas = chartInfo.instance.canvas;
        canvas.style.width = `${newWidth}px`;
        canvas.style.height = `${newHeight}px`;
        // chartInfo.instance.update();
      }
    };

    // データ更新関数
    const updateChartData = () => {
      // ====================== 外気グラフデータ生成 ======================
      let outsideCounter = 0;
      let outsideBarWorkPlaces = []
      outWorkPlaces.value = []; // 作業場所の値を格納する箱（外気）
      outsideTempertures.value = []; // 温度の値を格納する箱（外気）
      outsideRelativeHumidities.value = []; // 相対湿度の値を格納する箱（外気）
      outsideAirCurrents.value = []; // 気流の値を格納する箱（外気）
      outsideLineCo2s.value = []; // 折れ線グラフの二酸化炭素の値を格納する箱（外気）
      outsideBarCo2s.value = []; // 棒グラフ二酸化炭素の値を格納する箱（外気）
      outsideLineCos.value = []; // 折れ線グラフの一酸化炭素の値を格納する箱（外気）
      outsideBarCos.value = []; // 棒グラフの一酸化炭素の値を格納する箱（外気）
      outsideLineFloatPowderDusts.value = []; //折れ線グラフの浮遊粉塵量の値を格納する箱（外気）
      outsideBarFloatPowderDusts.value = []; // 棒グラフの浮遊粉塵量の値を格納する箱（外気）

      outsideAirWorkPlaces.value.forEach(function (t_measurement_work_place) {
        outWorkPlaces.value.push(t_measurement_work_place.work_place_nm);
        
        // 棒グラフの二酸化炭素のデータの作成
        outsideBarCo2s.value.push(null)
        outsideBarCo2s.value.push(null)
        outsideBarCo2s.value.push(t_measurement_work_place.co2_avg);
        outsideBarCo2s.value.push(null)
        
          // 棒グラフの一酸化炭素のデータの作成
        outsideBarCos.value.push(null)
        outsideBarCos.value.push(null)
        outsideBarCos.value.push(t_measurement_work_place.co_avg);
        outsideBarCos.value.push(null)
        
          // 棒グラフの浮遊粉塵量のデータの作成
        outsideBarFloatPowderDusts.value.push(null)
        outsideBarFloatPowderDusts.value.push(null)
        outsideBarFloatPowderDusts.value.push(t_measurement_work_place.float_powder_dust_avg);
        outsideBarFloatPowderDusts.value.push(null)

        // 測定回数が2回の報告書でもグラフの位置がずれないよう、3行に揃えてから積む
        contentMinimumRows(t_measurement_work_place).forEach(function (contentRow) {
          const t_measurement_work_place_content = contentRow || {};
          // 折れ線グラフで表示する値を作成
          outsideTempertures.value.push(t_measurement_work_place_content.dry_bulb);
          outsideRelativeHumidities.value.push(t_measurement_work_place_content.relative_humidity);
          outsideAirCurrents.value.push(null);
          // outsideAirCurrents.value.push(t_measurement_work_place_content.air_current);
          outsideLineCo2s.value.push(t_measurement_work_place_content.co2_itt);
          outsideLineCos.value.push(t_measurement_work_place_content.co_itt);
          outsideLineFloatPowderDusts.value.push(t_measurement_work_place_content.float_powder_dust_itt);

          // カウンタをインクリメント
          outsideCounter++;

          // 作業場所ごとにスペースを開けるため、3つデータを格納したら4つ目にはnullを入れて対応する
          if (outsideCounter % 3 === 0) {
            outsideTempertures.value.push(null);
            outsideRelativeHumidities.value.push(null);
            outsideAirCurrents.value.push(null);
            outsideLineCo2s.value.push(null);
            outsideLineCos.value.push(null);
            outsideLineFloatPowderDusts.value.push(null);
          }
        });
      });

      const outsideLineXValuesArray = [];
      for (let t = 0; t <= 8; t++) {
        outsideLineXValuesArray.push(t);
        if (t % 3 === 2) {
          outsideLineXValuesArray.push(t); // 3の倍数の時はもう一度pushする
        } 
      }

      let outsideWpBarCnt = 0;
      const outsideBarXValuesArray = [];
      for (let t = 0; t <= 6; t++) {
        outsideBarXValuesArray.push(t);
        outsideBarWorkPlaces.push(null)
        if (t % 3 === 2) {
          outsideBarXValuesArray.push(t + Math.floor(t / 3)); // 3の倍数の時はもう一度pushする
        }else if (t % 3 === 1){
          outsideBarWorkPlaces.push(outsideAirWorkPlaces.value[outsideWpBarCnt]?.work_place_nm)
          outsideWpBarCnt++;
        }
      }

      // ここで各作業場所ごとのデータセットを生成
      const outsideTempertureChartDataset = {
        labels: outsideLineXValuesArray,
        datasets: [{
          label: '温度',
          data: outsideTempertures.value,
          backgroundColor: '#3d5788',
          borderColor: '#3d5788',
          borderWidth: 2,
          pointRadius: 2, // 点の半径を2に設定
          pointHoverRadius: 2, // ホバー時の点の半径も2に設定
        }],
        workPlaces: outWorkPlaces.value,
        unit: '℃'
      };

      const outsideRelativeHumidityChartDataset = {
        labels: outsideLineXValuesArray,
        datasets: [{
          label: '相対湿度',
          data: outsideRelativeHumidities.value,
          backgroundColor: '#3d5788',
          borderColor: '#3d5788',
          borderWidth: 2,
          pointRadius: 2, // 点の半径を2に設定
          pointHoverRadius: 2, // ホバー時の点の半径も2に設定
        }],
        workPlaces: outWorkPlaces.value,
        unit: '%'
      };

      const outsideAirCurrentChartDataset = {
        labels: outsideLineXValuesArray,
        datasets: [{
          label: '気流',
          data: outsideAirCurrents.value,
          backgroundColor: '#3d5788',
          borderColor: '#3d5788',
          borderWidth: 2,
          pointRadius: 2, // 点の半径を2に設定
          pointHoverRadius: 2, // ホバー時の点の半径も2に設定
        }],
        workPlaces: outWorkPlaces.value,
        unit: 'm/秒'
      };
      
      const outsideCo2ChartDataset = {
        labels: outsideBarXValuesArray,
        datasets: [
          {
            label: '二酸化炭素',
            data: outsideLineCo2s.value, // 元の折れ線グラフデータ
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            type: 'line', // 折れ線グラフを指定
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '二酸化炭素',
            data: outsideBarCo2s.value, // 元の折れ線グラフデータ
            backgroundColor: '#abbbda',
            borderColor: '#677ca3',
            borderWidth: 1,
            type: 'bar', // 折れ線グラフを指定
            barThickness: 12
          }
        ],
        workPlaces: outsideBarWorkPlaces,
        unit: 'PPM'
      };

      const outsideCoChartDataset = {
        labels: outsideBarXValuesArray,
        datasets: [
          {
            label: '一酸化炭素',
            data: outsideLineCos.value,
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            type: 'line', // 折れ線グラフを指定
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '一酸化炭素',
            data: outsideBarCos.value, // 元の折れ線グラフデータ
            backgroundColor: '#abbbda',
            borderColor: '#677ca3',
            borderWidth: 1,
            type: 'bar', // 折れ線グラフを指定
            barThickness: 12
          }
        ],
        workPlaces: outsideBarWorkPlaces,
        unit: 'PPM'
      };

      const outsideFloatPowderDustChartDataset = {
        labels: outsideBarXValuesArray,
        datasets: [
          {
            label: '浮遊粉塵量',
            data: outsideLineFloatPowderDusts.value,
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            type: 'line', // 折れ線グラフを指定
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '浮遊粉塵量',
            data: outsideBarFloatPowderDusts.value, // 元の折れ線グラフデータ
            backgroundColor: '#abbbda',
            borderColor: '#677ca3',
            borderWidth: 1,
            type: 'bar', // 折れ線グラフを指定
            barThickness: 12
          }
        ],
        workPlaces: outsideBarWorkPlaces,
        unit: 'mg/㎥'
      };

      // ここでデータセットを適切な配列に追加
      outsideTempertureChartData.value = outsideTempertureChartDataset;
      outsideRelativeHumidityChartData.value = outsideRelativeHumidityChartDataset;
      outsideAirCurrentChartData.value = outsideAirCurrentChartDataset;
      outsideCo2ChartData.value = outsideCo2ChartDataset;
      outsideCoChartData.value = outsideCoChartDataset;
      outsideFloatPowderDustChartData.value = outsideFloatPowderDustChartDataset;

      // ====================== 外気グラフデータ生成 ======================

      // ====================== 室内グラフデータ生成 ======================
      tempertureChartData.value = [];
      relativeHumidityChartData.value = [];
      airCurrentChartData.value = [];
      co2ChartData.value = [];
      coChartData.value = [];
      floatPowderDustChartData.value = [];

      // 室内データがない場合に領域だけ作るためにからデータを入れておく
      if (SeparateWorkPlaceCount.value == 0){
        SeparateWorkPlaceCount.value = 1
        SeparateWorkPlaces.value = [[ // 測定場所の配列を初期化
          { // 外気の作業場所を示すオブジェクトを追加
            work_place_nm: '',
            sort_no: null,
            co2_avg: null,
            co_avg: null,
            float_powder_dust_avg: null,
            t_measurement_work_place_contents: [{
                                                  dry_bulb: null,
                                                  relative_humidity: null,
                                                  air_current: null,
                                                  co2_itt: null,
                                                  co_itt: null,
                                                  float_powder_dust_itt: null
                                                },
                                                {
                                                  dry_bulb: null,
                                                  relative_humidity: null,
                                                  air_current: null,
                                                  co2_itt: null,
                                                  co_itt: null,
                                                  float_powder_dust_itt: null
                                                },
                                                {
                                                  dry_bulb: null,
                                                  relative_humidity: null,
                                                  air_current: null,
                                                  co2_itt: null,
                                                  co_itt: null,
                                                  float_powder_dust_itt: null
                                                }]
          }]]
      }

      for (let i = 0; i < SeparateWorkPlaceCount.value; i++) {
        
        let counter = 0;
        workPlaces.value = []; // 作業場所の値を格納する箱（折れ線グラフ）
        let barWorkPlaces = [] // 作業場所の値を格納する箱（折れ線 + 棒グラフ）
        tempertures.value = []; // 温度の値を格納する箱
        relativeHumidities.value = []; // 相対湿度の値を格納する箱
        airCurrents.value = []; // 気流の値を格納する箱
        lineCo2s.value = []; // 折れ線グラフの二酸化炭素の値を格納する箱
        barCo2s.value = []; // 棒グラフ二酸化炭素の値を格納する箱
        lineCos.value = []; // 折れ線グラフの一酸化炭素の値を格納する箱
        barCos.value = []; // 棒グラフの一酸化炭素の値を格納する箱
        lineFloatPowderDusts.value = []; //折れ線グラフの浮遊粉塵量の値を格納する箱
        barFloatPowderDusts.value = []; // 棒グラフの浮遊粉塵量の値を格納する箱

        SeparateWorkPlaces.value[i].forEach(function (t_measurement_work_place) {
          workPlaces.value.push(t_measurement_work_place.work_place_nm);
          
          // 棒グラフの二酸化炭素のデータの作成
          barCo2s.value.push(null)
          barCo2s.value.push(null)
          barCo2s.value.push(t_measurement_work_place.co2_avg);
          barCo2s.value.push(null)
          
           // 棒グラフの一酸化炭素のデータの作成
          barCos.value.push(null)
          barCos.value.push(null)
          barCos.value.push(t_measurement_work_place.co_avg);
          barCos.value.push(null)
          
           // 棒グラフの一酸化炭素のデータの作成
          barFloatPowderDusts.value.push(null)
          barFloatPowderDusts.value.push(null)
          barFloatPowderDusts.value.push(t_measurement_work_place.float_powder_dust_avg);
          barFloatPowderDusts.value.push(null)

          // 測定回数が2回の報告書でもグラフの位置がずれないよう、3行に揃えてから積む
          contentMinimumRows(t_measurement_work_place).forEach(function (contentRow) {
            const t_measurement_work_place_content = contentRow || {};
            // 折れ線グラフで表示する値を作成
            tempertures.value.push(t_measurement_work_place_content.dry_bulb);
            relativeHumidities.value.push(t_measurement_work_place_content.relative_humidity);
            airCurrents.value.push(t_measurement_work_place_content.air_current);
            lineCo2s.value.push(t_measurement_work_place_content.co2_itt);
            lineCos.value.push(t_measurement_work_place_content.co_itt);
            lineFloatPowderDusts.value.push(t_measurement_work_place_content.float_powder_dust_itt);

            // カウンタをインクリメント
            counter++;

            // 作業場所ごとにスペースを開けるため、3つデータを格納したら4つ目にはnullを入れて対応する
            if (counter % 3 === 0) {
              tempertures.value.push(null);
              relativeHumidities.value.push(null);
              airCurrents.value.push(null);
              lineCo2s.value.push(null);
              lineCos.value.push(null);
              lineFloatPowderDusts.value.push(null);
            }
          });
        });

        const lineXValuesArray = [];
        for (let t = 0; t <= 42; t++) {
          lineXValuesArray.push(t);
          if (t % 3 === 2) {
            lineXValuesArray.push(t); // 3の倍数の時はもう一度pushする
          } 
        }

        let wpBarCnt = 0;
        const barXValuesArray = [];
        for (let t = 0; t <= 42; t++) {
          barXValuesArray.push(t);
          barWorkPlaces.push(null)
          if (t % 3 === 2) {
            barXValuesArray.push(t + Math.floor(t / 3)); // 3の倍数の時はもう一度pushする
          }else if (t % 3 === 1){
            barWorkPlaces.push(SeparateWorkPlaces.value[i][wpBarCnt]?.work_place_nm)
            wpBarCnt++;
          }
        }

        // ここで各作業場所ごとのデータセットを生成
        const tempertureDataset = {
          labels: lineXValuesArray,
          datasets: [{
            label: '温度' + (i + 1),
            data: tempertures.value,
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '最低基準温度',
            data: Array(100).fill(tempertureMinStd.value), // 13個の要素すべてを16で埋める
            borderColor: 'red',
            borderWidth: 2,
            pointRadius: 0, // ポイントの表示をなくす
          },
          {
            label: '最高基準温度',
            data: Array(100).fill(tempertureMaxStd.value), // 13個の要素すべてを32で埋める
            borderColor: 'red',
            borderWidth: 2,
            pointRadius: 0, // ポイントの表示をなくす
          }],
          workPlaces: workPlaces.value,
          unit: '℃'
        };

        const relativeHumidityChartDataset = {
          labels: lineXValuesArray,
          datasets: [{
            label: '相対湿度' + (i + 1),
            data: relativeHumidities.value,
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '最低基準相対湿度',
            data: Array(100).fill(relativeHumidityMinStd.value), // 13個の要素すべてを16で埋める
            borderColor: 'red',
            borderWidth: 2,
            pointRadius: 0, // ポイントの表示をなくす
          },
          {
            label: '最高基準相対湿度',
            data: Array(100).fill(relativeHumidityMaxStd.value), // 13個の要素すべてを32で埋める
            borderColor: 'red',
            borderWidth: 2,
            pointRadius: 0, // ポイントの表示をなくす
          }],
          workPlaces: workPlaces.value,
          unit: '%'
        };

        const airCurrentChartDataset = {
          labels: lineXValuesArray,
          datasets: [{
            label: '気流' + (i + 1),
            data: airCurrents.value,
            backgroundColor: '#3d5788',
            borderColor: '#3d5788',
            borderWidth: 2,
            pointRadius: 2, // 点の半径を2に設定
            pointHoverRadius: 2, // ホバー時の点の半径も2に設定
          },
          {
            label: '最高基準気流',
            data: Array(100).fill(airCurrentMaxStd.value), // 13個の要素すべてを16で埋める
            borderColor: 'red',
            borderWidth: 2,
            pointRadius: 0, // ポイントの表示をなくす
          }],
          workPlaces: workPlaces.value,
          unit: 'm/秒'
        };
        
        const co2ChartDataset = {
          labels: barXValuesArray,
          datasets: [
            {
              label: '二酸化炭素' + (i + 1),
              data: lineCo2s.value, // 元の折れ線グラフデータ
              backgroundColor: '#3d5788',
              borderColor: '#3d5788',
              borderWidth: 2,
              type: 'line', // 折れ線グラフを指定
              pointRadius: 2, // 点の半径を2に設定
              pointHoverRadius: 2, // ホバー時の点の半径も2に設定
            },
            {
              label: '二酸化炭素' + (i + 1),
              data: barCo2s.value, // 元の折れ線グラフデータ
              backgroundColor: '#abbbda',
              borderColor: '#677ca3',
              borderWidth: 1,
              type: 'bar', // 折れ線グラフを指定
              barThickness: 12
            },
            {
              label: '最高基準二酸化炭素',
              data: Array(100).fill(co2MaxStd.value), // 13個の要素すべてを16で埋める
              borderColor: 'red',
              borderWidth: 2,
              pointRadius: 0, // ポイントの表示をなくす
            }
          ],
          workPlaces: barWorkPlaces,
          unit: 'PPM'
        };

        const coChartDataset = {
          labels: barXValuesArray,
          datasets: [
            {
              label: '一酸化炭素' + (i + 1),
              data: lineCos.value,
              backgroundColor: '#3d5788',
              borderColor: '#3d5788',
              borderWidth: 2,
              type: 'line', // 折れ線グラフを指定
              pointRadius: 2, // 点の半径を2に設定
              pointHoverRadius: 2, // ホバー時の点の半径も2に設定
            },
            {
              label: '一酸化炭素' + (i + 1),
              data: barCos.value, // 元の折れ線グラフデータ
              backgroundColor: '#abbbda',
              borderColor: '#677ca3',
              borderWidth: 1,
              type: 'bar', // 折れ線グラフを指定
              barThickness: 12
            },
            {
              label: '最高基準一酸化炭素',
              data: Array(100).fill(coMaxStd.value), // 13個の要素すべてを16で埋める
              borderColor: 'red',
              borderWidth: 2,
              pointRadius: 0, // ポイントの表示をなくす
            }
          ],
          workPlaces: barWorkPlaces,
          unit: 'PPM'
        };

        const floatPowderDustChartDataset = {
          labels: barXValuesArray,
          datasets: [
            {
              label: '浮遊粉塵量' + (i + 1),
              data: lineFloatPowderDusts.value,
              backgroundColor: '#3d5788',
              borderColor: '#3d5788',
              borderWidth: 2,
              type: 'line', // 折れ線グラフを指定
              pointRadius: 2, // 点の半径を2に設定
              pointHoverRadius: 2, // ホバー時の点の半径も2に設定
            },
            {
              label: '浮遊粉塵量' + (i + 1),
              data: barFloatPowderDusts.value, // 元の折れ線グラフデータ
              backgroundColor: '#abbbda',
              borderColor: '#677ca3',
              borderWidth: 1,
              type: 'bar', // 折れ線グラフを指定
              barThickness: 12
            },
            {
              label: '最高基準浮遊粉塵量',
              data: Array(200).fill(floatPowderDustMaxStd.value), // 13個の要素すべてを16で埋める
              borderColor: 'red',
              borderWidth: 2,
              pointRadius: 0, // ポイントの表示をなくす
            }
          ],
          workPlaces: barWorkPlaces,
          unit: 'mg/㎥'
        };

        // ここでデータセットを適切な配列に追加
        tempertureChartData.value.push(tempertureDataset);
        relativeHumidityChartData.value.push(relativeHumidityChartDataset);
        airCurrentChartData.value.push(airCurrentChartDataset);
        co2ChartData.value.push(co2ChartDataset);
        coChartData.value.push(coChartDataset);
        floatPowderDustChartData.value.push(floatPowderDustChartDataset);
      }
      // ====================== 室内グラフデータ生成 ======================

      drawCharts();
      outsideDrawCharts();
      updateCanvasWidth();
      updateoutsideCanvasWidth();
    };

    // ウィンドウの幅を基に`canvasWidth`を更新する関数（外気）
    const updateoutsideCanvasWidth = () => {
      // 外気のグラフの横幅
      const outSideCanvasParent = document.querySelector('.out-enter-type .canvas-parent');
      outsideCanvasWidth.value = outSideCanvasParent.offsetWidth;

      outsideChartInstances.value.forEach(chartInfo => {
        outsideUpdateChartSize(chartInfo.id, outsideCanvasWidth.value, 180);
      });
      // ここでより具体的な計算を行うことも可能
    };

    // ウィンドウの幅を基に`canvasWidth`を更新する関数（室内）
    const updateCanvasWidth = () => {
      // 室内のグラフの横幅
      const canvasParent = document.querySelector('.result-type .canvas-parent');
      canvasWidth.value = canvasParent.offsetWidth;


      chartInstances.value.forEach(chartInfo => {
        updateChartSize(chartInfo.id, canvasWidth.value, 180);
      });
      // ここでより具体的な計算を行うことも可能
    };

    // 日時フォーマット変換メソッド
    const formatDateTime = (dateTime) => {
      if (dateTime == 'undefined') {
        return '';
      }

      const date = new Date(dateTime);
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    };

    // パターン1: 年月日
    function formatDatePattern1(dateString) {
      if (dateString === '') {
        return '';
      }

      const date = new Date(dateString);
      const { era, year } = calculateJapaneseEra(date, '令和', '平成', '昭和');
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${era}${year}年${month}月${day}日`;
    }

    // パターン2: 年月度
    function formatDatePattern2(dateString) {
      if (dateString === '') {
        return '';
      }

      const date = new Date(dateString);
      const { era, year } = calculateJapaneseEra(date, '令和', '平成', '昭和');
      const month = date.getMonth() + 1;
      return `${era}${year}年${month}月度`;
    }

    // パターン2: 年号年.月.日
    function formatDatePattern3(dateString) {
      if (dateString === '') {
        return '';
      }

      const date = new Date(dateString);
      const { era, year } = calculateJapaneseEra(date, 'R', 'H', 'S');
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${era}${year}.${month}.${day}`;
    }

    function calculateJapaneseEra(date, reiwa, heisei, showa) {
      if (date === '') {
        return '';
      }

      const eras = [
        { start: new Date(2019, 4 - 1, 1), name: reiwa },
        { start: new Date(1989, 1 - 1, 8), name: heisei },
        { start: new Date(1926, 12 - 1, 25), name: showa },
        // 必要に応じて他の元号を追加
      ];

      for (let era of eras) {
        if (date >= era.start) {
          const yearOfEra = date.getFullYear() - era.start.getFullYear() + 1;
          return { era: era.name, year: yearOfEra };
        }
      }

      return { era: '不明', year: date.getFullYear() };
    }

    // 小数第二位で四捨五入するメソッドの追加
    const roundToSecondDecimal = (num) => {
      if (num === 'undefined' || num === '' || num === null) {
        return '';
      }

      return Math.round(num * 100) / 100;
    };

    // 少数第n位までを表示させるメソッド
    const roundToDecimal = (value, init_val, decimals) => {
      if (!value) {
        return init_val;
      }
      value = parseFloat(value);
      return value.toFixed(decimals);
    };

    const pdfContent = ref(null); // PDFに出力したいコンテンツの参照

    const generatePDF = async () => {
      Chart.unregister(breakLabelsPlugin);
      isLoading.value = true;  // ローディング開始
      // コンポーネントの内容を含むdivを作成
      const el = document.createElement('div');
      document.body.appendChild(el);

      // MeasurementReportDetailPDFコンポーネントをマウント
      const app = createApp(MeasurementReportDetailPDF, {
        measurement_reportId: measurement_reportId
      });
      app.mount(el);

      // コンポーネントがマウントされてDOMが更新された後に次のフレームで実行
      await nextTick();

      // PDF生成オプション
      const options = {
        margin: [0, 0],
        filename: 'report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 1.5 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      };

      // DOMが更新されるのを確実に待つ
      setTimeout(async () => {
        try {
          // html2pdfを使ってPDFを生成
          await html2pdf().set(options).from(el).save();
        } catch (error) {
          console.error('PDF生成エラー:', error);
        } finally {
          // マウントしたアプリケーションをクリーンアップし、ローディングを解除
          app.unmount();
          document.body.removeChild(el);
          isLoading.value = false;  // ローディング終了
        }
      }, 2000); // 1000ms のディレイを設ける
    };

    onMounted(() => {
      fetchMeasurementReport().then(() => {
        drawCharts();
        outsideDrawCharts();
        updateCanvasWidth();
        updateoutsideCanvasWidth();
      });
    });

    return {
      title,
      measurement_report,
      workPlaceMinimumRows,
      deviceMinimumRows,
      contentMinimumRows,
      upperTempLabel,
      lowerTempLabel,
      upperTempValue,
      lowerTempValue,
      insideAirWorkPlaces,
      viewSeparateWorkPlaces,
      viewSeparateWorkPlaceCount,
      tempertureMinStd,
      tempertureMaxStd,
      relativeHumidityMinStd,
      relativeHumidityMaxStd,
      airCurrentMaxStd,
      co2MaxStd,
      coMaxStd,
      floatPowderDustMaxStd,
      SeparateWorkPlaceCount,
      tempertureChartCanvases,
      relativeHumidityChartCanvases,
      airCurrentChartCanvases,
      co2ChartCanvases,
      coChartCanvases,
      floatPowderDustChartCanvases,
      outsideTempertureChartCanvas,
      outsideRelativeHumidityChartCanvas,
      outsideAirCurrentChartCanvas,
      outsideCo2ChartCanvas,
      outsideCoChartCanvas,
      outsideFloatPowderDustChartCanvas,
      formatDateTime,
      formatDatePattern1,
      formatDatePattern2,
      formatDatePattern3,
      roundToSecondDecimal,
      roundToDecimal,
      pdfContent,
      generatePDF,
      isLoading
    };
  }
};
</script>

<style scoped>
/* ローディング */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: white;
  z-index: 100;
}
.spinner {
  border: 6px solid #f3f3f3;
  border-radius: 50%;
  border-top: 6px solid #3498db;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
/* ローディング */

/* 表紙部分 */
.main-cover{
  padding: 40px 60px;
}
.main-cover .thank-you{
  font-size: 40px;
  font-family: serif;
}
.main-cover h1{
  margin: 80px auto 80px auto;
  letter-spacing: 1rem;
  font-family: serif;
}
.main-cover h1 .report-title{
  display: inline-block;
  padding: 40px 120px;
  border: 3px solid #000;
  border-radius: 16px;
}
.main-cover .contents{
  margin-left: 30%;
}
.main-cover .contents h4{
  margin-bottom: 28px;
  font-size: 24px;
  font-family: serif;
}
.main-cover .contents  h4 .subtitle{
  display: inline-block;
  width: 200px;
}
.main-cover .contents  h4 .information{
  display: inline-block;
}
/* 表紙部分 */

/* 空気環境調査概要 */
.overview{
  margin-top: 100px;
  padding: 40px;
}
.overview .title-contents h2{
  width: calc(100% - 480px);
  font-size: 40px;
  letter-spacing: 1rem;
  font-family: serif;
}
.overview .title-contents table th,
.overview .title-contents table td{
  padding: 8px;
  border: 1px solid #000;
  font-family: serif;
}
.overview .title-contents table th{
  width: 120px;
  text-align-last: justify;
}
.overview .title-contents table td{
  width: 360px;
}
.overview .main-contents table th,
.overview .main-contents table td{
  border: 1px solid #000;
  font-family: serif;
  vertical-align: middle;
}
.overview .main-contents table th{
  padding: 20px;
  font-size: 20px;
  font-weight: normal;
  text-align: center;
}
.overview .main-contents table th.major-head{
  padding: 20px;
  font-size: 24px;
  font-weight: normal;
  vertical-align: middle;
}
.overview .main-contents table td{
  text-align: center;
}
.overview .main-contents table td.major-body{
  width: 42px;
  padding: 20px 0;
  text-align: center;
  font-size: 20px;
  font-weight: normal;
  writing-mode: vertical-lr;
}
.overview .main-contents table tr.special-comment-area{
  position: relative;
  min-height: 200px;
  height: 200px;
}
.overview .main-contents table td.comment,
.overview .main-contents table td.special-comment{
  text-align: left;
}
.overview .main-contents table th.comment-title,
.overview .main-contents table td.comment{
  min-width: 400px;
}
.overview .main-contents table td.special-comment{
  vertical-align: top !important;
}
.overview .main-contents table td.special-comment .sign-box01,
.overview .main-contents table td.special-comment .sign-box02{
  position: absolute;
  bottom: -1px;
  display: inline-block;
  width: 100px;
  height: 100px;
  border: 1px solid #000;
  border-right: none;
  border-bottom: none;
}
.overview .main-contents table td.special-comment .sign-box01{
  right: 100px;
}
.overview .main-contents table td.special-comment .sign-box02{
  right: 0;
}
/* 空気環境調査概要 */

/* 作業場所と測定機器 */
.place-and-device{
  padding: 80px 60px;
}
.place-and-device .measure-place{
  width: 40%;
}
.place-and-device .measure-place table th,
.place-and-device .measure-place table td{
  padding: 4px 8px;
  border: 1px solid #000;
  font-size: 18px;
  font-family: serif;
}
.place-and-device .measure-place table th{
  text-align: center;
  font-weight: normal;
}
.place-and-device .measure-place table th.place-title{
  font-size: 24px;
  letter-spacing: 3rem;
  text-indent: 3rem;
}
.place-and-device .measure-place table th.place-name,
.place-and-device .measure-place table td.place-name{
  min-width: 300px;
}
.place-and-device .measure-device{
  width: 60%;
}
.place-and-device .measure-device table th,
.place-and-device .measure-device table td{
  padding: 4px 8px;
  font-size: 18px;
  border: 1px solid #000;
  font-family: serif;
}
.place-and-device .measure-device table th{
  text-align: center;
  font-weight: normal;
}
.place-and-device .measure-device table th.device-title{
  font-size: 24px;
  letter-spacing: 3rem;
  text-indent: 3rem;
}
.place-and-device .measure-device table th.device-contents,
.place-and-device .measure-device table td.device-contents{
  min-width: 360px;
}
.place-and-device .measure-device table td{
  height: 110px;
}
/* 作業場所と測定機器 */

/* 環境測定結果 */
.measure-result{
  padding: 40px 60px;
}
.measure-result .title-area h2{
  width: 64%;
  font-weight: normal;
  letter-spacing: 3rem;
  text-indent: 3em;
  font-family: serif;
}
.measure-result .title-area table{
  width: 36%;
}
.measure-result .title-area table th,
.measure-result .title-area table td{
  padding: 4px 8px;
  border: 1px solid #000;
  font-size: 16px;
  font-weight: normal;
  font-family: serif;
}
.measure-result .title-area table th{
  width: 120px;
  text-align-last: justify;
}
.measure-result .title-area table th.weather{
  width: 52px;
  vertical-align: top;
}
.measure-result .measure-contents table th,
.measure-result .measure-contents table td{
  padding: 2px;
  font-size: 12px;
  font-weight: bold;
  border: 1px solid #000;
  font-family: serif;
}
.measure-result .measure-contents table th.work-place,
.measure-result .measure-contents table th.remarks{
  vertical-align: middle;
  letter-spacing: 1.5rem;
  text-indent: 1.5em;
}
.measure-result .measure-contents table td{
  vertical-align: middle;
}
.measure-contents table.contents-table,
.measure-contents table thead{
  border: 3px solid #000;
}
.meadure-contents table td.work-place{
  border-right: 3px solid #000 !important;
}
.border-bottom-bold{
  border-bottom: 3px solid #000;
}
.work-place{
  max-width: 160px;
  width: 160px;
  min-width: 160px;
}
.time{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.person-count{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.dry-blub,
.wet-blub{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.relative-humidity{
  max-width: 60px;
  width: 60px;
  min-width: 60px;
}
.air-current{
  max-width: 60px;
  width: 60px;
  min-width: 60px;
}
.co2{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.co{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.floating-dust-amount{
  max-width: 40px;
  width: 40px;
  min-width: 40px;
}
.illuminance{
  max-width: 52px;
  width: 52px;
  min-width: 52px;
}
.noise{
  max-width: 52px;
  width: 52px;
  min-width: 52px;
}
.other{
  max-width: 60px;
  width: 60px;
  min-width: 60px;
}
.pt-80{
  padding-top: 80px;
}
/* 環境測定結果 */

/* 温熱条件測定結果 */
.thermal-condition-results{
  position: relative;
  padding: 40px 60px;
}
.thermal-condition-results h2{
  margin-bottom: 60px;
  font-family: serif;
}
.thermal-condition-results .precedent{
  padding-right: 40px;
}
.thermal-condition-results .precedent table th,
.thermal-condition-results .precedent table td{
  padding: 4px 8px;
  text-align: center;
  border: 1px solid #000;
  font-family: serif;
}
.thermal-condition-results .precedent table td .graph-symbol .circle{
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #3d5788;
  border-radius: 50%;
}
.thermal-condition-results .precedent table td .graph-symbol .line{
  display: inline-block;
  width: 32px;
  height: 2px;
  background-color: #3d5788;
}
.thermal-condition-results .precedent table td .graph-symbol-tip p{
  margin: 0;
  font-size: 10px;
  font-family: serif;
}
.thermal-condition-results .result-area{
  margin: 20px 0;
}
.thermal-condition-results .result-area .measure-unit{
  margin: 60px 0;
}
.thermal-condition-results .result-area .measure-unit .result-type{
  width: 78%;
}
.thermal-condition-results .result-area .measure-unit .result-type h3{
  text-align: center;
  font-size: 20px;
  font-weight: normal;
  writing-mode: vertical-lr;
  text-align-last: justify;
  font-family: serif;
}
.thermal-condition-results .result-area .measure-unit .result-type .canvas-parent{
  width: calc(100% - 20px);
}
.thermal-condition-results .result-area .measure-unit .result-type .graph-area canvas{
  position: absolute;
  top: 0;
  left: 0;
}
.thermal-condition-results .result-area .measure-unit .out-enter-type{
  width: 22%;
}
.thermal-condition-results .result-area .measure-unit .not-applicable-area{
  position: relative;
}
.thermal-condition-results .result-area .measure-unit .not-applicable-area .not-applicable-overlay{
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 240px;
  padding: 12px;
  background-color: #5d5d5f;
  opacity: 0.8;
  text-align: center;
  color: #fff;
  font-family: serif;
}
.thermal-condition-results .outside-mark{
  position: absolute;
  bottom: 0;
  right: 132px;
  margin-bottom: 0;
  padding: 12px 40px;
  font-weight: bold;
  font-size: 24px;
  border: 3px solid #000;
  font-family: serif;
}
/* 温熱条件測定結果 */

.detail-item {
  margin-bottom: 10px;
}

.btn-area{
  text-align: center;
}

.btn-area-fixed{
  position: fixed;
  top: 64px;
  right: 60px;
}

.text-red{
  color: #ff0000;
  font-weight: bold !important;
}

.page-break {
  page-break-after: always;
}
</style>
